import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { isNil } from "lodash";
import { makeStyles } from "@mui/styles";

import { palette } from "../../theme/palette";
import { NONE } from "../../consts/strings";

import { setSelectedLocation } from "../../reducers/ownApp/";
import { selectedLocationSelector } from "../../reducers/ownApp/selectors";
import { setJumpTo } from "../../reducers/map/thunkActions";
import { setNoCoordinatesLocation } from "../../reducers/matrixProfile/thunkActions";

import { Text, View } from "../Base";
import LocationLabel from "./LocationLabel";
import RouteLabel from "./RouteLabel";
import DriverLabel from "./DriverLabel";

const useStyles = makeStyles(() => ({
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "0.1rem",
    marginBottom: "0.1rem",
    padding: "0.5rem 0.35rem 0.5rem 0.25rem",
    background: palette.typography.white.neutral,
  },
  selected: {
    background: palette.typography.white.disabled,
  },
  top: {
    display: "flex",
    gap: "0.25rem",
    marginBottom: "-0.3rem",
  },
  labelContainer: {
    paddingTop: "0.05rem",
  },
  name: {
    marginLeft: "0.25rem",
    fontSize: "18px",
    textTransform: "uppercase",
    color: palette.white,
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  detailsContainer: {
    display: "flex",
    margin: "0 0.5rem 0.25rem 0.5rem",
    color: "white",
    lineHeight: "1.25rem",
    gap: "0.5rem",
  },
  routeContainer: {
    display: "flex",
    margin: "0 0.5rem",
    paddingTop: "0.15rem",
    color: "white",
    lineHeight: "1.25rem",
    gap: "0.5rem",
    borderWidth: "1px",
    borderTopStyle: "solid",
    borderColor: palette.white,
  },
  volumeContainer: {
    display: "flex",
    margin: "0 0.5rem",
    paddingTop: "0.15rem",
    color: "white",
    lineHeight: "1.25rem",
    gap: "0.5rem",
    borderWidth: "1px",
    borderTopStyle: "solid",
    borderColor: palette.white,
  },
  left: {
    minWidth: "5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    fontSize: "0.875rem",
  },
  right: {
    display: "flex",
    flexDirection: "column",
    fontSize: "0.875rem",
  },
}));

const LocationItem = ({ location, route, driver, volume }) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const selectedLocation = useSelector(selectedLocationSelector);
  const isSelected = location.locationCode === selectedLocation?.locationCode;

  const onClick = () => {
    dispatch(setSelectedLocation(location));
    const { latitude, longitude } = location;
    if (!isNil(latitude) && !isNil(longitude))
      dispatch(
        setJumpTo({
          center: [longitude, latitude],
          zoom: 16,
        })
      );
    else dispatch(setNoCoordinatesLocation(location.locationCode));
  };

  return (
    <View
      className={clsx(styles.card, isSelected && styles.selected)}
      onClick={onClick}
    >
      <View className={styles.top}>
        <View className={styles.labelContainer}>
          <LocationLabel location={location} />
        </View>
        <Text className={styles.name} data-testid="location-name">
          {location.name}
        </Text>
      </View>
      <View className={styles.detailsContainer}>
        <View className={styles.left}>
          <View>Postcode:</View>
          <View>Latitude:</View>
          <View>Longitude:</View>
        </View>
        <View className={styles.right}>
          <View>{location.postcode}</View>
          <View>{location.latitude || "???"}</View>
          <View>{location.longitude || "???"}</View>
        </View>
      </View>
      <View className={styles.routeContainer}>
        <View className={styles.left}>
          <View>Route:</View>
          <View>Driver:</View>
        </View>
        <View className={styles.right}>
          <View>{route ? <RouteLabel route={route} /> : NONE}</View>
          <View>
            <DriverLabel driver={driver} />
            {driver?.driverName || NONE}
          </View>
        </View>
      </View>
      {volume ? (
        <View className={styles.volumeContainer}>
          <View className={styles.left}>
            <View>Parcels:</View>
            <View>Stops:</View>
          </View>
          <View className={styles.right}>
            <View>{volume.parcels || 0}</View>
            <View>{volume.stops || 0}</View>
          </View>
        </View>
      ) : null}
    </View>
  );
};

export default LocationItem;
